

























import Scheduler from '@/core/components/proxies/Scheduler.vue';
import { getTableCaption } from '@/core/components/table';
import { Component, Prop } from 'vue-property-decorator';
import { Page } from '@/core/components/controls';
import { PlannedOperation, PlannedOperationType } from '@/dto';
import { OperationsChartPageArgs } from './types';
import { localize, date } from '@/core/filters';
import { formatDate, Inject } from '@/core/helpers';
import {
    CONTRACTS,
    WindowsServiceContract,
    EventsServiceContract,
    CustomEvents
    } from '@/core/contracts';
import { UserState } from '@/store';

@Component({
    components: {
        Scheduler
    }
})
export default class OperationsChartPage extends Page {
    @Prop({ default: () => ({}) }) public args!: OperationsChartPageArgs;

    public selectedOperationId: any;
    public activeBox: boolean = false;

    @Inject(CONTRACTS.WindowsService)
    private _windowsService?: WindowsServiceContract;
    @Inject(CONTRACTS.EventsService)
    private _eventsService?: EventsServiceContract;

    public get pageName(): string {
        return getTableCaption('operations-chart-control');
    }

    public async mounted() {
        this._eventsService!.on(
            CustomEvents.PlannedOperationsPageClosed,
            this.onPlannedOperationsPageClosed
        );
    }

    public async beforeDestroy() {
        this._eventsService!.off(
            CustomEvents.PlannedOperationsPageClosed,
            this.onPlannedOperationsPageClosed
        );
    }

    public onPlannedOperationsPageClosed() {
        if (this.args.parent._isDestroyed) {
            const zoomLevel = (this.$refs.scheduler as any).schedulerEngine.zoomLevel;
            switch (zoomLevel) {
                case 23: {
                    (this.$refs.scheduler as any).schedulerEngine.zoomOut();
                    (this.$refs.scheduler as any).schedulerEngine.zoomIn();
                    break;
                }

                default: {
                    (this.$refs.scheduler as any).schedulerEngine.zoomIn();
                    (this.$refs.scheduler as any).schedulerEngine.zoomOut();
                    break;
                }
            }
        }
    }

    public get resources() {
        return this.args.operations
            ? Array.from(
                  new Set(this.args.operations.map(x => x.mainResource.name))
              ).map(x => ({ id: x, name: x }))
            : [];
    }

    public get eventsConfig() {
        const { startDate, finishDate } = this.getBounds(
            this.args.operations || []
        );

        const events = this.args.operations
            ? this.args.operations
                  .filter(x => x.startTime !== x.finishTime)
                  .map(x => ({
                      resourceId: x.mainResource.name,
                      startDate: this.boundDate(
                          formatDate(x.startTime!, false, false),
                          startDate,
                          finishDate
                      ),
                      endDate: this.boundDate(
                          formatDate(x.finishTime!, false, false),
                          startDate,
                          finishDate
                      ),
                      linkedResource: x.linkedResource && x.linkedResource.name,
                      startSite: x.startSite && x.startSite.name,
                      finishSite: x.finishSite && x.finishSite.name,
                      name: localize(`EventType_${x.type}`),
                      eventColor: this.getOperationColor(x),
                      id: x.id,
                      type: x.type,
                      timeDuration: x.timeDuration,
                      productTypes: x.productTypes,
                      volume: x.volume,
                      area: x.area,
                      packs: x.packs,
                      weight: x.weight
                  }))
                  .filter(x => x.startDate !== x.endDate)
            : [];

        return {
            startDate,
            finishDate,
            events
        };
    }

    public get columns() {
        return [
            {
                name: localize('OperationsChartResourceColumn'),
                field: 'name',
                width: 150
            }
        ];
    }

    public get schedulerConfig() {
        return {
            ...this.eventsConfig,
            resources: this.resources,
            columns: this.columns,
            readOnly: true,
            viewPreset: 'hourAndDay',
            minHeight: '20em',
            rowHeight: 31,
            locale: this.locale,
            features: {
                pan: true,
                eventDragCreate: false,
                eventTooltip: {
                    header: {
                        titleAlign: 'start'
                    },
                    template: this.tooltipTemplate
                }
            },
            listeners: {
                eventClick: (e: any) => {
                    this.selectedOperationId = e.eventRecord.id;
                    this.activeBox = this.isActiveBox(e.eventRecord);
                }
            }
        };
    }

    public get locale() {
        switch (UserState?.current?.culture) {
            case 'en-US':
                return 'En';
            default :
                return 'Ru';
        }
    }

    public showPlannedOperationsDetailTable(id: string) {
        const args = { operation: this.getCurrentOperation() };

        this._windowsService!.open('PlannedOperationsDetailPage', args);
    }

    public isActiveBox(operation: PlannedOperation): boolean {
        return operation.packs! > 0;
    }

    private getCurrentOperation() {
        return this.args.operations?.find(operation => operation.id === this.selectedOperationId);
    }

    private boundDate(
        value?: string,
        min?: string,
        max?: string
    ): string | undefined {
        if (!value) {
            return value;
        }

        const dateObj = new Date(value);

        if (min && dateObj < new Date(min)) {
            return min;
        }

        if (max && dateObj > new Date(max)) {
            return max;
        }

        return value;
    }

    private getBounds(
        operations: PlannedOperation[]
    ): { startDate: string; finishDate: string } {
        // filter inactive intervals because they often have incredible huge finish date
        const [startDate, finishDate] = operations
            .filter(
                operation => operation.type !== PlannedOperationType.INACTIVE
            )
            .reduce((acc: any[], operation) => {
                const eventStart = new Date(formatDate(operation.startTime!, false, false ));
                const eventEnd = new Date(formatDate(operation.finishTime!, false, false));
                acc[0] =
                    acc[0] === undefined || eventStart < acc[0]
                        ? eventStart
                        : acc[0];
                acc[1] =
                    acc[1] === undefined || eventEnd > acc[1]
                        ? eventEnd
                        : acc[1];
                return acc;
            }, []) as string[];
        return {
            startDate,
            finishDate
        };
    }

    private getOperationColor(operation: PlannedOperation) {
        const colors: { [key in PlannedOperationType]: string } = {
            Loading: 'orange',
            Unloading: 'yellow',
            Relocation: 'green',
            Waiting: 'gray',
            Inactive: 'gray',
            EmptyRelocation: 'teal',
            Preparation: 'pink',
            Completion: 'PaleVioletRed'
        };
        return operation.type && operation.type in colors
            ? colors[operation.type]
            : undefined;
    }

    private tooltipTemplate(data: any) {
        const event = data.eventRecord.data;
        const fields = [];

        // Resource name
        fields.push(`
            <dt>${localize('OperationsChartResourceColumn')}:</dt>
            <dd>
                ${event.resourceId}
            </dd>
        `);

        // Linked resource name
        if (event.linkedResource) {
            fields.push(`
                <dt>${localize('OperationsChartLinkedResourceColumn')}:</dt>
                <dd>
                    ${event.linkedResource}
                </dd>
            `);
        }

        // Location
        if (event.startSite !== event.finishSite) {
            fields.push(`
                <dt>${localize('OperationsChartRelocationColumn')}:</dt>
                <dd>
                    ${event.startSite} -> ${event.finishSite}
                </dd>
            `);
        } else if (event.startSite) {
            fields.push(`
                <dt>${localize('OperationsChartLocationColumn')}:</dt>
                <dd>
                    ${event.startSite}
                </dd>
            `);
        }

        // Times.
        fields.push(`
            <dt>${localize('OperationsChartTimeSpanColumn')}:</dt>
            <dd>
                ${event.startDate.toLocaleString()} - ${event.endDate.toLocaleString()}
            </dd>
        `);

        fields.push(`
            <dt>Длительность:</dt>
            <dd>${data.eventRecord.timeDuration}</dd>
        `);

        if (event.area !== undefined) {
            fields.push(`
                <dt>${localize('GridColumnTitleSumArea')}:</dt>
                <dd>
                    ${event.area.toFixed(2)}
                </dd>
            `);
        }

        if (event.volume) {
            fields.push(`
                <dt>${localize('GridColumnTitleSumVolume')}:</dt>
                <dd>
                    ${event.volume.toFixed(2)}
                </dd>
            `);
        }

        if (event.weight) {
            fields.push(`
                <dt>${localize('GridColumnTitleSumWeight')}:</dt>
                <dd>
                    ${event.weight.toFixed(2)}
                </dd>
            `);
        }

        if (event.productTypes) {
            fields.push(`
                <dt>${localize('GridColumnTitleProductType')}:</dt>
                <dd>
                    ${event.productTypes}
                </dd>
            `);
        }

        return `<dl>${fields.join('\n')}</dl>`;
    }
}
